<script>
  // Reading one source's export into the Pod (docs/ref-belonging-plan.md,
  // step 7.7): the rows of the stored export not read yet, or a new export,
  // uploaded and read in one go. Its next file keeps the mapping and the
  // watermark; a file that no longer fits the mapping is refused here.
  import Section from '../components/Section.svelte';
  import Panel from '../components/Panel.svelte';
  import Alert from '../components/Alert.svelte';
  import Button from '../components/Button.svelte';
  import DataTable from '../components/DataTable.svelte';
  import Async from '../components/Async.svelte';
  import HelpDrawer from '../components/HelpDrawer.svelte';
  import { getSources, getSource, getDatasets, saveSource, runIngest } from '../lib/api.js';
  import { parseAndProfileCsv } from '../../../lib/disclosure_core.js';
  import { newDataProblems } from '../../../lib/source_setup.js';
  import { selectPending } from '../../../lib/ingest_pending.js';
  import { HELP } from '../lib/help.js';
  import { watermarkText } from '../lib/datasets.js';
  import { nav } from '../lib/nav.svelte.js';
  import { mayVisit } from '../lib/session.svelte.js';

  // Derived, not read once: going from one source's Ingest to another's
  // keeps this screen mounted.
  const sourceId = $derived(nav.params.id ?? '');
  const q = $derived(encodeURIComponent(sourceId));
  async function load() {
    const [{ sources }, { connector, mapping }, { datasets }] = await Promise.all([getSources(), getSource(sourceId), getDatasets()]);
    const source = sources.find((s) => s.id === sourceId);
    if (!source) throw new Error(`No source “${sourceId}”.`);
    return { source, connector, mapping, fills: datasets.filter((d) => d.sources.some((s) => s.id === sourceId)) };
  }
  const data = $derived(load());
  // After a run, the figures read again replace the shown ones in place,
  // without the loading state in between.
  let fresh = $state(null);

  // The file chosen, before it is uploaded.
  let file = $state(null); // { name, mediaType, csvText, profile }
  let fileError = $state(null);
  let picker = $state(null);
  async function choose(event) {
    fileError = null; file = null; result = null;
    const chosen = event.currentTarget.files?.[0];
    if (!chosen) return;
    try {
      if (chosen.size > 5 * 1024 * 1024) throw new Error('The file is over the 5 MB limit.');
      const csvText = await chosen.text();
      file = { name: chosen.name, mediaType: chosen.type || 'text/csv', csvText, profile: parseAndProfileCsv(csvText) };
    } catch (e) { fileError = e.message; }
  }

  let running = $state(false);
  let progress = $state(null); // { done, total, message }
  let result = $state(null);
  let error = $state(null);
  // What was chosen or done for one source does not carry over to the next.
  $effect.pre(() => { sourceId; fresh = null; file = null; fileError = null; result = null; error = null; progress = null; });
  const barPct = $derived(progress && progress.total ? (progress.done / progress.total) * 100 : 0);

  async function ingest() {
    progress = { done: 0, total: 0, message: 'Starting…' };
    result = await runIngest(sourceId, { onProgress: (job) => { if (job.progress) progress = job.progress; } });
  }
  // `upload` first stores the chosen file; `andIngest` then reads it in.
  async function go({ upload = false, andIngest = true } = {}) {
    running = true; error = null; result = null;
    try {
      if (upload) {
        progress = { done: 0, total: 0, message: 'Uploading…' };
        await saveSource({ id: sourceId, label: sourceId, fileName: file.name, mediaType: file.mediaType, csvText: file.csvText });
        file = null;
        if (picker) picker.value = '';
      }
      if (andIngest) await ingest();
      fresh = await load();
    } catch (e) { error = e.message; }
    finally { running = false; progress = null; }
  }
  const rows = (n) => `${n} ${n === 1 ? 'row' : 'rows'}`;
</script>

<HelpDrawer {...HELP['source/ingest']} />

<Async promise={fresh ?? data} loading="Reading the source…">
  {#snippet children({ source, connector, mapping, fills })}
    {@const waiting = source.mode === 'awaiting-upload'}
    {@const snapshot = source.mode === 'snapshot'}
    <h1 class="wf2-title wf2-title-level-1">Ingest: {source.label}</h1>
    <p class="wf2-subtitle">
      Fills {#each fills as d, i}{#if i}, {/if}<a href={`#/data/dataset?id=${encodeURIComponent(d.id)}`}>{d.label}</a>{:else}no dataset yet{/each}
      · {#if waiting}no file yet{:else}{rows(source.rowsInSource)} in the stored export
      · {snapshot ? (source.watermark ? `last read ${watermarkText(source.watermark)}` : 'not read yet') : (source.watermark ? `read up to ${watermarkText(source.watermark)}` : 'not read yet')}{/if}
    </p>

    {#if !waiting}
      <Section title={snapshot ? 'The stored export' : 'New rows waiting'}>
        <Panel>
          {#if snapshot}
            <p class="wf2-text">This export has no timestamp column, so every ingest reads all its rows again. Records already in your Pod are overwritten, not duplicated.</p>
            <Button variant="primary" onclick={() => go()} disabled={running}>Ingest all {rows(source.rowsInSource)}</Button>
          {:else if source.pending}
            <p class="wf2-text">{rows(source.pending)} of the stored export {source.pending === 1 ? 'has' : 'have'} not been read yet.</p>
            <Button variant="primary" onclick={() => go()} disabled={running}>Ingest {rows(source.pending)}</Button>
          {:else}
            <p class="wf2-text">Up to date: nothing waiting.</p>
          {/if}
        </Panel>
      </Section>
    {/if}

    <Section title={waiting ? 'Upload its first file' : 'Upload a new export'}>
      <Panel>
        <p class="wf2-text">
          {#if waiting}This source was prepared for you: its mapping is set up and waits for the file.
          {:else}Its next file is read with the same mapping. Rows already read are skipped, unless the export has no timestamp column.{/if}
          The file stays private: only what the mapping says is counted.
        </p>
        <label class="wf2-field">CSV file <input class="wf2-control" type="file" accept=".csv,text/csv" bind:this={picker} onchange={choose} disabled={running} /></label>
        {#if fileError}<Alert title="Could not read the file" variant="danger"><p>{fileError}</p></Alert>{/if}
        {#if file}
          {@const problems = newDataProblems(connector, mapping, file.profile.headers)}
          {@const fresh = selectPending(file.profile.rows, connector).pending.length}
          {@const total = file.profile.rows.length}
          {#if problems.missing.length}
            <Alert title={waiting ? 'The prepared mapping expects other columns' : 'This file does not fit the mapping'} variant={waiting ? 'warning' : 'danger'}>
              <p>Missing: {problems.missing.join(', ')}.</p>
              <p>{waiting ? 'You can upload it anyway, and fix the mapping before ingesting.' : 'Fix the mapping first, or choose another file.'}</p>
              {#if mayVisit('source/mapping')}<Button variant="secondary" to={`source/mapping?id=${q}`}>Open the mapping</Button>{/if}
            </Alert>
          {/if}
          {#if problems.added.length}<p class="wf2-hint">New columns, not in the mapping yet: {problems.added.join(', ')}. They are left out until you label them.</p>{/if}
          {#if connector.sourceKey === '@row'}<p class="wf2-hint">Records are identified by row number, so keep the row order of earlier exports: a row that moves becomes a different record.</p>{/if}
          {#if !problems.missing.length}
            <p class="wf2-text">{file.name}: {rows(total)}{snapshot ? ', all read again' : waiting ? '' : fresh === total ? ', all of them new' : `, ${fresh} of them new`}.</p>
            <Button variant="primary" onclick={() => go({ upload: true, andIngest: snapshot || waiting || fresh > 0 })} disabled={running}>
              {snapshot || waiting || fresh > 0 ? 'Upload and ingest' : 'Upload'}
            </Button>
          {:else if waiting}
            <Button variant="secondary" onclick={() => go({ upload: true, andIngest: false })} disabled={running}>Upload without ingesting</Button>
          {/if}
        {/if}
      </Panel>
    </Section>

    {#if progress}
      <div class="wf2-tablewrap" style="margin: var(--space-3) 0">
        <p class="wf2-caption">{progress.message}{progress.total ? ` — ${progress.done}/${progress.total}` : ''}</p>
        <div class="bar-track"><div class="bar" style="width:{barPct}%"></div></div>
      </div>
    {/if}
    {#if error}<Alert title="Ingest failed" variant="danger"><p class="wf2-text">{error}</p></Alert>{/if}
    {#if result}
      <Alert title="Ingested" variant="success">
        <p class="wf2-text">
          Read {rows(result.pending)}. {source.label} now has {result.recordsAfter ?? '—'} records{result.failed ? `; ${result.failed} could not be written` : ''}.
          Your private cubes are counted again; what partners see changes only when a manager sends them an updated copy.
        </p>
      </Alert>
    {/if}

    <div class="wf2-row" style="gap:12px">
      <Button variant="secondary" to="sources">Back to Sources</Button>
    </div>
  {/snippet}
</Async>
